import { useContext, useCallback, useMemo } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { dateDifference } from 'lib/datetime/dateUtils'
import { rem } from 'polished'
import LayoutContainer from 'components/Common/LayoutContainer'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import Clickable from 'components/Common/Clickable/Clickable'
import Image from 'components/Common/Image'
import PromotionLabel from 'LuxKit/Label/PromotionLabel'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import getOfferFeatureIcon from 'lib/offer/getOfferFeatureIcon'
import formatOfferInclusionDescription from 'lib/offer/formatOfferInclusionDescription'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import OfferUrgencyCountdownLabel from 'components/Common/Labels/OfferUrgencyCountdownLabel'
import DealPageHeadingText from 'components/Common/DealsTile/DealPageHeadingText'
import CruiseDealTermsConditionsModal from './CruiseDealTermsConditionsModal'
import { scrollToId } from 'lib/window/scrollUtils'
import { formatMiddleDotSeparator } from 'LuxKit/intl/separators'

const ImageBackgroundWrapper = styled.div`
  position: absolute;
  top: 0;
  width: 100%;
  height: 60%;

  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    width: 100%;
    height: 80%;
    background: linear-gradient(to top, rgb(54 58 69), rgb(54 58 69 / 80%), rgb(54 58 69 / 0%));
    z-index: 1;
  }

  ${mediaQueryUp.tablet} {
    right: 0;
    width: 50%;
    height: 100%;

    &::before {
      top: 0;
      left: 0;
      width: ${rem(400)};
      height: 100%;
      background: linear-gradient(to right, rgb(54 58 69), rgb(54 58 69 / 0%));
    }
  }

  ${mediaQueryUp.desktop} {
    width: 60%;
  }
`

const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  padding-top: ${rem(32)};
  padding-bottom: ${rem(20)};

  ${mediaQueryUp.tablet} {
    padding-top: ${rem(28)};
    padding-bottom: ${rem(40)};
    width: 50%;
  }

  ${mediaQueryUp.desktop} {
    width: 30%;
  }
`

const CruiseDealHeroWrapper = styled.div`
  position: relative;
  background-color: ${props => props.theme.palette.neutral.surface.darkBackground};
`

const DEFAULT_HERO_IMAGE_ID = 'nmiblelbjmu4o2nj0m34'

interface Props {
  cruiseDeal: App.Cruises.CruiseDeal
}

function CruiseDealHero({ cruiseDeal }: Props) {
  const intl = useIntl()
  const sep = formatMiddleDotSeparator(intl)
  const showModal = useContext(ModalContext)

  const handleScrollToOffers = useCallback(() => {
    scrollToId('cruise-deal-offer-list')
  }, [])

  const showTermsAndConditionsModal = useCallback(() => {
    showModal(<CruiseDealTermsConditionsModal
      termsAndConditions={cruiseDeal.termsAndConditions}
      onViewOffers={handleScrollToOffers}
    />)
  }, [showModal, cruiseDeal.termsAndConditions, handleScrollToOffers])

  const shouldShowCountdown = useMemo(() => {
    return dateDifference(new Date(cruiseDeal.endDate), new Date()).days < 5
  }, [cruiseDeal.endDate])
  const offerCountdownPrefix = intl.formatMessage({
    id: 'aD7zby5ii2Scn3GvQ15M',
    defaultMessage: 'Offer',
    description: 'Prefix label used before the cruise deal countdown timer',
  })

  const hasHighlightedInclusion = useMemo(() => {
    return cruiseDeal.inclusions.some((inclusion) => inclusion.isHighlighted)
  }, [cruiseDeal.inclusions])

  return <CruiseDealHeroWrapper>
    <ImageBackgroundWrapper>
      <Image
        alt={intl.formatMessage({
          id: '8PZO5XJPRC3IJnX3VMW2',
          defaultMessage: 'Hero image',
          description: 'Alt text for the cruise deal hero background image',
        })}
        id={cruiseDeal.imageId || DEFAULT_HERO_IMAGE_ID}
        fit="center"
      />
    </ImageBackgroundWrapper>

    <LayoutContainer size="xl">
      <ContentWrapper>
        <Group direction="vertical" gap={16}>
          <Image
            alt={intl.formatMessage({
              id: '16VZPR1JrqWu7D_e-qoT',
              defaultMessage: 'Cruise line logo',
              description: 'Alt text for the cruise line brand logo on the deal page',
            })}
            id={cruiseDeal.cruiseLine.whiteImageId}
            height={75}
          />

          <Group direction="vertical" gap={8} horizontalAlign="start">
            <PromotionLabel variant="premium" icon={<SolidFireIcon />}>
              {intl.formatMessage({
                id: 'uY0pTTGI743RovSi4d3b',
                defaultMessage: 'Special offer',
                description: 'Promotion label for cruise deal special offer in the cruises section',
              })}
            </PromotionLabel>
            <Heading colour="dark-contrast" variant="heading2">
              <DealPageHeadingText text={cruiseDeal.heading} shouldBreakLines />
            </Heading>
          </Group>

          <Group direction="vertical" gap={8}>
            <Group direction="vertical" gap={4}>
              {cruiseDeal.inclusions.map((inclusion, index) => {
                // if there is no highlighted inclusion, the first inclusion should be highlighted
                const isHighlighted = inclusion.isHighlighted || (!hasHighlightedInclusion && index === 0)

                const Icon = getOfferFeatureIcon(inclusion.icon || 'check')

                return <BodyTextBlock
                  key={inclusion.id}
                  variant="medium"
                  weight={isHighlighted ? 'bold' : 'normal'}
                  colour={isHighlighted ? 'highlight-primary' : 'dark-contrast'}
                  startIcon={Icon ? <Icon /> : undefined}
                >
                  {formatOfferInclusionDescription(inclusion.description, intl)}
                </BodyTextBlock>
              })}
            </Group>

            <Group direction="horizontal" gap={8} horizontalAlign="space-between" verticalAlign="center">
              <Group direction="vertical" tabletDirection="horizontal" gap={4} tabletGap={8}>
                {shouldShowCountdown && <OfferUrgencyCountdownLabel endDate={cruiseDeal.endDate} prefixLabel={offerCountdownPrefix} tagPlace="cruise-deal-hero" />}

                <Group direction="vertical" tabletDirection="horizontal" tabletGap={8}>
                  {!shouldShowCountdown && <BodyText variant="medium" colour="dark-contrast">
                    <FormattedMessage
                      id="uXbmiRm9tvRNtJlUes4A"
                      defaultMessage="Offer ends on {date}"
                      description="Label displaying the expiry date for a cruise deal offer in the listing or card"
                      values={{ date: <FormatDateTime value={cruiseDeal.endDate} format="dmy-casual-short" /> }}
                    />
                  </BodyText>}

                  {!shouldShowCountdown && <CSSBreakpoint min="tablet">
                    <BodyText variant="medium" colour="dark-contrast">
                      {sep}
                    </BodyText>
                  </CSSBreakpoint>}

                  <Clickable onClick={showTermsAndConditionsModal}>
                    <BodyText variant="medium" colour="dark-contrast" underline>
                      {intl.formatMessage({
                        id: '9QK_nLe33AUacf5_TuRz',
                        defaultMessage: 'T&Cs apply',
                        description: 'Link text to view terms and conditions for cruise deal',
                      })}
                    </BodyText>
                  </Clickable>
                </Group>
              </Group>

              <CSSBreakpoint only="mobile">
                <TextButton
                  kind="secondary"
                  variant="dark"
                  onClick={handleScrollToOffers}
                >
                  {intl.formatMessage({
                    id: 'g63tUezkSpnrqo7cRYl-',
                    defaultMessage: 'View offers',
                    description: 'Button text to scroll to cruise deal offers section',
                  })}
                </TextButton>
              </CSSBreakpoint>
            </Group>
          </Group>
        </Group>
      </ContentWrapper>
    </LayoutContainer>
  </CruiseDealHeroWrapper>
}

export default CruiseDealHero
